import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ShieldCheck, 
  Maximize2, 
  Minimize2, 
  X, 
  Lock, 
  FileText,
  Mail,
  Phone,
  MapPin,
  ExternalLink
} from 'lucide-react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFullScreen, onClose]);

  const sections = [
    {
      num: "1",
      title: "Commitment to Privacy",
      content: (
        <p className="print-text">
          This website and service platform is owned and operated by Randon Brown, LLC (<span className="text-brand-400 font-mono">randonbrown.com</span>). We are dedicated to safeguarding client privacy, maintaining transparent operations, and securing all submitted diagnostic and project information.
        </p>
      )
    },
    {
      num: "2",
      title: "Information We Collect",
      content: (
        <div className="space-y-2 print-text">
          <p>We collect only the information you voluntarily submit when requesting technical services or initiating website projects:</p>
          <ul className="list-disc list-inside space-y-1.5 text-slate-400 pl-1">
            <li><strong className="text-slate-200">Technical Service &amp; Repair Quotes:</strong> Full name, email address, phone number, physical address/zip code, service delivery mode, urgency level, detailed issue description, and digital authorization signatures.</li>
            <li><strong className="text-slate-200">Website Onboarding Project Briefs:</strong> Business name, domain requirements, contact info, brand assets, package selection, core page specifications, cloud repository links, and timestamped digital signatures.</li>
            <li><strong className="text-slate-200">Administrative Sign-In:</strong> Authorized Google identity credentials used strictly for owner dashboard management.</li>
          </ul>
        </div>
      )
    },
    {
      num: "3",
      title: "How Your Information Is Processed",
      content: (
        <div className="space-y-1.5 print-text">
          <p>Information submitted through our forms is utilized solely to:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1 mt-1">
            <li>Diagnose hardware/software faults, formulate repair quotes, and schedule service.</li>
            <li>Design, develop, configure DNS, and launch custom websites.</li>
            <li>Dispatch automated submission alerts to <span className="text-brand-300 font-mono">help@randonbrown.com</span> and send branded receipts directly to clients.</li>
            <li>Communicate regarding project deliverables, diagnostics, and invoice processing.</li>
          </ul>
        </div>
      )
    },
    {
      num: "4",
      title: "Secure Data Storage & Transactional Email",
      content: (
        <p className="print-text">
          All form transmissions are protected via HTTPS / 256-bit SSL encryption. Submissions are securely stored in Google Cloud Firestore with server-enforced security rules. Email notifications and client receipts are dispatched via Resend's verified domain infrastructure (<span className="text-brand-300 font-mono">randonbrown.com</span>).
        </p>
      )
    },
    {
      num: "5",
      title: "Absolute Prohibition on Data Sales",
      content: (
        <p className="print-text">
          We do <strong className="text-white">not</strong> sell, rent, monetize, or trade client personal data to third-party data brokers, advertising networks, or lead generators. All information remains strictly confidential.
        </p>
      )
    },
    {
      num: "6",
      title: "External Services & Portals",
      content: (
        <div className="space-y-1.5 print-text">
          <p className="mb-1">Our platform interfaces with reputable operational providers:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 pl-1">
            <li><strong className="text-slate-200">Zoho Secure Invoicing:</strong> Client estimates, itemized invoices, and payment receipts are hosted on Zoho’s PCI-compliant portal (<span className="text-brand-400 font-mono">invoice.zohosecure.com</span>).</li>
            <li><strong className="text-slate-200">Outbound Links:</strong> External links to YouTube, LinkedIn, and client websites operate under their respective privacy policies.</li>
          </ul>
        </div>
      )
    },
    {
      num: "7",
      title: "Zero Advertising Cookies & Local Storage",
      content: (
        <p className="print-text">
          We do not use tracking pixels, cross-site beacons, or marketing cookies. Browser local storage is used solely for essential functional preferences (cookie consent preferences, dark mode, and interactive sandbox states).
        </p>
      )
    },
    {
      num: "8",
      title: "Your Data Rights & Contact",
      content: (
        <div className="space-y-2 print-text">
          <p>
            You may request a copy of your records, request corrections, or ask for permanent record deletion from our database at any time by contacting:
          </p>
          <div className="p-3 bg-neutral-900/80 rounded-xl border border-neutral-800 text-[11px] font-mono text-slate-300 space-y-1">
            <div>Email: <a href="mailto:help@randonbrown.com" className="text-brand-400 underline">help@randonbrown.com</a></div>
            <div>Phone: <a href="tel:3149443074" className="text-emerald-400">(314) 944-3074</a></div>
            <div>Location: Texas County, Missouri, United States</div>
          </div>
        </div>
      )
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="printable-modal-wrapper fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4 md:p-6 overflow-hidden">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="printable-modal-backdrop absolute inset-0 bg-black/85 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal Card */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.2 }}
            className={`printable-modal-card relative flex flex-col bg-[#0c0c0c] border border-neutral-800 shadow-2xl z-10 text-slate-300 transition-all duration-200 ${
              isFullScreen 
                ? 'fixed inset-0 w-full h-full max-w-none max-h-none rounded-none border-0' 
                : 'w-full max-w-4xl lg:max-w-5xl max-h-[90vh] rounded-2xl sm:rounded-3xl overflow-hidden m-2 sm:m-0'
            }`}
          >
            {/* Header */}
            <div className="p-4 sm:p-5 border-b border-neutral-900 flex justify-between items-center shrink-0 bg-neutral-950/95 sticky top-0 z-20 print-hide">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-500/10 text-brand-400 rounded-xl border border-brand-500/20">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-white font-display flex items-center gap-2">
                    Privacy Policy
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      Zero Tracking
                    </span>
                  </h3>
                  <p className="text-[10px] sm:text-[11px] font-mono text-slate-500">Randon Brown IT &amp; Website Solutions</p>
                </div>
              </div>

              {/* Action Buttons: Pop Out / Full Screen, Close */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className="px-3 py-1.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 shadow-sm"
                  title={isFullScreen ? "Exit Full Screen" : "Pop Out to Full Screen"}
                >
                  {isFullScreen ? (
                    <>
                      <Minimize2 className="w-3.5 h-3.5 text-brand-400" />
                      <span className="hidden sm:inline text-xs font-mono">Restore</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="w-3.5 h-3.5 text-brand-400" />
                      <span className="hidden sm:inline text-xs font-mono">Pop Out</span>
                    </>
                  )}
                </button>

                <button 
                  type="button"
                  onClick={onClose}
                  className="p-1.5 hover:bg-neutral-900 rounded-full transition-colors text-slate-400 hover:text-white cursor-pointer"
                  aria-label="Close Privacy Policy"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Print Header (Visible ONLY when printing) */}
            <div className="hidden print:block print-header">
              <h1 className="print-header-title">Randon Brown, LLC | Privacy Policy</h1>
              <p className="print-header-sub">Official Website &amp; Technical Services Privacy Policy • randonbrown.com</p>
              <p className="print-header-sub">Effective Date: October 2026 • Contact: help@randonbrown.com • (314) 944-3074</p>
            </div>

            {/* Scrollable Modal Content */}
            <div className="printable-modal-body p-5 sm:p-7 md:p-8 overflow-y-auto custom-scrollbar flex-1 space-y-4 text-xs sm:text-sm leading-relaxed text-left">
              {/* Summary Callout Banner */}
              <div className="p-4 rounded-2xl bg-brand-500/[0.07] border border-brand-500/20 flex items-start gap-3 print:border-gray-300">
                <Lock className="w-4 h-4 text-brand-400 shrink-0 mt-0.5 print-hide" />
                <p className="text-slate-300 text-xs leading-relaxed print-text">
                  We collect strictly necessary operational details to fulfill technical support and custom website projects. We never sell, trade, or monetize your information, and we do not use third-party marketing trackers or ad beacons.
                </p>
              </div>

              {/* Sections List */}
              <div className="space-y-4 pt-1">
                {sections.map((section) => (
                  <div
                    key={section.num}
                    className="print-section-item p-4 sm:p-5 rounded-2xl bg-neutral-950/80 border border-neutral-900/90 hover:border-brand-500/30 transition-all space-y-2"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-lg bg-brand-500/10 text-brand-400 border border-brand-500/20 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                        {section.num}
                      </span>
                      <h4 className="font-bold text-sm sm:text-base text-white font-display print-title">
                        {section.title}
                      </h4>
                    </div>
                    <div className="pl-0 sm:pl-8">
                      {section.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-neutral-950/95 border-t border-neutral-900 flex items-center justify-between gap-3 shrink-0 print-hide">
              <span className="text-[11px] font-mono text-slate-500 hidden sm:inline">
                DIRECT INQUIRIES: <a href="mailto:help@randonbrown.com" className="text-brand-400 hover:underline">HELP@RANDONBROWN.COM</a>
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <button 
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md"
                >
                  Close Policy
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyPolicyModal;
